/**
 * frontend/src/components/InviteArtistPanel.jsx
 * SoundDAO Sprint 6A — Reusable artist invite/nomination panel
 *
 * Extracted from ArtistPickStep.jsx Path B. Used by:
 *   - DiscoverTab (Invite subtab)
 *   - ArtistPickStep (nominate tab)
 *
 * Props:
 *   userId       — current user's UUID
 *   onDraftDM    — optional callback(dmPrompt) to open chatbot with a draft DM
 *   onInviteComplete — optional callback({ handle, platform, inviteUrl }) after successful generation
 *   compact      — boolean, if true hides the heading/subtext (for onboarding flow)
 */

import React, { useState, useCallback } from 'react';
import { authFetch } from '../lib/authFetch';
import { useRates, sound, plus } from '../lib/rates';
import './InviteArtistPanel.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

export default function InviteArtistPanel({ userId, onDraftDM, onInviteComplete, compact }) {
  const rates = useRates();
  const [platform, setPlatform]         = useState('x');
  const [handle, setHandle]             = useState('');
  const [inviteUrl, setInviteUrl]       = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [copied, setCopied]             = useState(false);

  const sanitizedHandle = handle.replace(/^@/, '').trim();
  const canGenerate     = sanitizedHandle.length >= 2;

  const handleGenerate = useCallback(async () => {
    if (!canGenerate || actionLoading) return;
    setActionLoading(true);
    try {
      // Prompt 1.5: the nominator is the token holder.
      const res = await authFetch(`${API_BASE}/api/nominations/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          artist_handle: sanitizedHandle,
          platform,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setInviteUrl(data.invite_url);
        if (onInviteComplete) {
          onInviteComplete({ handle: sanitizedHandle, platform, inviteUrl: data.invite_url });
        }
      }
    } catch (err) {
      console.warn('[InviteArtistPanel] generate invite failed:', err);
    }
    setActionLoading(false);
  }, [canGenerate, sanitizedHandle, platform, actionLoading, onInviteComplete]);

  const handleCopy = useCallback(() => {
    if (!inviteUrl) return;
    navigator.clipboard.writeText(inviteUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [inviteUrl]);

  const handleDraftDM = useCallback(() => {
    const dmPrompt = `Write me a short DM to send to @${sanitizedHandle} on ${platform === 'x' ? 'X (Twitter)' : 'Instagram'} inviting them to join Big Creative as an artist. Keep it casual and genuine.`;
    if (onDraftDM) {
      onDraftDM(dmPrompt);
    } else {
      localStorage.setItem('sounddao_pending_chat_message', dmPrompt);
    }
  }, [sanitizedHandle, platform, onDraftDM]);

  return (
    <div className="invite-panel">
      {!compact && (
        <>
          <h3 className="invite-panel-title">Get your favorite artist on Big Creative</h3>
          <p className="invite-panel-sub">
            Enter their handle — we'll generate a personal invite link.
            When they join, confirm their email and make a first post, you earn {sound(rates, 'recruit_creator')}.
          </p>
          <div className="invite-panel-reward">{plus(rates, 'recruit_creator')} SOUND</div>
        </>
      )}

      {/* Platform toggle */}
      <div className="invite-panel-platforms">
        <button
          className={`invite-panel-pill ${platform === 'x' ? 'invite-panel-pill--active' : ''}`}
          onClick={() => setPlatform('x')}
        >
          X (Twitter)
        </button>
        <button
          className={`invite-panel-pill ${platform === 'instagram' ? 'invite-panel-pill--active' : ''}`}
          onClick={() => setPlatform('instagram')}
        >
          Instagram
        </button>
      </div>

      {/* Handle input */}
      <input
        className="invite-panel-input"
        type="text"
        inputMode="url"
        autoCapitalize="none"
        autoCorrect="off"
        placeholder="@artisthandle"
        value={handle}
        onChange={e => setHandle(e.target.value)}
        maxLength={50}
      />

      {/* Generate button */}
      {!inviteUrl && (
        <button
          className="invite-panel-generate"
          onClick={handleGenerate}
          disabled={!canGenerate || actionLoading}
        >
          {actionLoading ? 'Generating…' : 'Generate invite link'}
        </button>
      )}

      {/* Result card */}
      {inviteUrl && (
        <div className="invite-panel-result">
          <p className="invite-panel-result-url">{inviteUrl}</p>
          <div className="invite-panel-result-btns">
            <button className="invite-panel-btn invite-panel-btn--copy" onClick={handleCopy}>
              {copied ? 'Copied!' : 'Copy link'}
            </button>
            <button className="invite-panel-btn invite-panel-btn--dm" onClick={handleDraftDM}>
              Draft DM
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
